// hooks/useCreateNewReservation.ts
import { useDispatch, useSelector } from "react-redux";
import { apartmentBookingApiSlice } from "../../api/apartment/apartmentApiSlice";
import { categoryFromSearch } from "../../modules/home/homeTiles";
import { categoryNoticeFor } from "../../api/apartment/categoryOutcome";
import ErrorTracker from "../helpers/errorTracker";
import { stationDayStart } from "./reservationWindow";
import { useSelectedLocationTimeZone } from "../../util/locationTimeZone";
import Logger from "../helpers/logger";
import {
  apartmentsLoadFailed,
  apartmentsLoadStarted,
  categoryNoticeSet,
  setAllApartment,
} from "../slice/apartmentRental/apartmentRentalSlice";
import {
  setLoader
} from "../slice/bookingCreation/bookingCreationSlice";
import { baseStore, RootState } from "../store"; // Adjust the import based on your file structure
import {
  notifyLoadFailure,
  queryResultError,
} from "../helpers/failureNotification";
import { getTenantName } from "../../util";
const fetchApartmentsByCityCall = async (
  city: string,
  fromDate: string,
  toDate: string,
  numberOfGuests: number,
  productCategory?: string
) => {
  const data = await baseStore.dispatch(
    apartmentBookingApiSlice.endpoints.getApartmentByCityList.initiate({
      city: city,
      fromDate: fromDate,
      toDate: toDate,
      numberOfGuests: numberOfGuests,
      ...(productCategory ? { productCategory } : {}),
    })
  );
  return data;
};

const fetchApartmentsByCityAndDistrictCall = async (
  city: string,
  district: string,
  fromDate: string,
  toDate: string,
  numberOfGuests: number,
  productCategory?: string
) => {
  const data = await baseStore.dispatch(
    apartmentBookingApiSlice.endpoints.getApartmentByCityAndDistrictList.initiate({
      city: city,
      district: district,
      fromDate: fromDate,
      toDate: toDate,
      numberOfGuests: numberOfGuests,
      ...(productCategory ? { productCategory } : {}),
    })
  );
  return data;
};

export const useFetchApartments = () => {
  const dispatch = useDispatch();

  const roomRentalLocation = useSelector(
    (state: RootState) => state.bookingCreation.roomRentalLocation
  );
  const checkInDate = useSelector(
    (state: RootState) => state.bookingCreation.orderData.checkInDate
  );
  const checkOutDate = useSelector(
    (state: RootState) => state.bookingCreation.orderData.checkOutDate
  );
  const numberOfGuests = useSelector(
    (state: RootState) => state.apartment.numberOfGuest
  );
  // The search area's clock (#3219) — the server counts nights there.
  const timeZone = useSelectedLocationTimeZone();

  const fetchApartments = async () => {
    try {
      dispatch(setLoader(true));
      dispatch(apartmentsLoadStarted());
      let apartments;
      let data;

      // ADR-0026 §A, the same contract as the venue flow: which category's
      // tile was tapped travels in the URL, so a refresh or a shared link
      // shows the same catalogue. Without this every apartment tile would
      // open the same list — two buttons telling the customer these are
      // different things and then showing that they are not.
      const productCategory = categoryFromSearch(window.location.search);
      // The same dates the booking is created with: station midnight of the
      // picked day (`stationDayStart`), so the room listed as free is checked
      // for exactly the window that gets booked.
      // The search only runs once both dates are picked; an absent date is
      // passed through as before, unconverted.
      const fromDate = (checkInDate ? stationDayStart(checkInDate, timeZone) : undefined) as string;
      const toDate = (checkOutDate ? stationDayStart(checkOutDate, timeZone) : undefined) as string;

      if (roomRentalLocation.district === "all") {
        data = await fetchApartmentsByCityCall(
          roomRentalLocation.city!, // Todo: refactor to not use !
          fromDate,
          toDate,
          numberOfGuests,
          productCategory
        );
      } else {
        data = await fetchApartmentsByCityAndDistrictCall(
          roomRentalLocation.city!,
          roomRentalLocation.district!,
          fromDate,
          toDate,
          numberOfGuests,
          productCategory
        );
      }
      // `initiate` resolves with `{ error }` instead of throwing, so a failed
      // search rendered as "no rooms available" (#1970). Return before
      // `setAllApartment`, which would clear the flag the step renders on and
      // overwrite the list with nothing.
      const failure = queryResultError(data);
      if (failure) {
        Logger.for("fetchApartments").error(failure);
        ErrorTracker.trackException(failure as Error);
        dispatch(apartmentsLoadFailed());
        notifyLoadFailure(dispatch, "book_location.error.fetch_apartments");
        dispatch(setLoader(false));
        return undefined;
      }
      if (data) {
        // `{ items, categoryNotice }` since #2764 F9/F10 — the list plus what
        // the server said about the `category` key (`categoryOutcome.ts`).
        apartments = data.data?.items;
      }
      // A result that carries no LIST is a failed search, not an empty one.
      //
      // QA #2793: the request returns 200 with 26 apartments and the screen
      // says "0 Apartments gefunden". `data.data` is undefined on the
      // hydration path although the HTTP call succeeded, and `setAllApartment`
      // then takes the slice's defensive branch and stores `[]` — a failure
      // rendered as the no-results screen, which is the one thing CLAUDE.md
      // says a failure must never do.
      //
      // The mechanism #2793 hit, since found: the account listener evicted
      // this cache when a stored session resolved mid-search, and `initiate`
      // then read an empty entry (`redux/accountScopedCaches.ts`). That is
      // fixed at its source; this guard stays, because any future eviction or
      // reset produces the same shape, and "I have no list" and "there is
      // nothing here" must reach the customer as different screens.
      // `apartmentsLoadFailed` still ANSWERS the search, so the #2791 hang
      // cannot come back through this path.
      if (!Array.isArray(apartments)) {
        Logger.for("fetchApartments").error(
          "search resolved without a list",
          data
        )
        dispatch(apartmentsLoadFailed());
        notifyLoadFailure(dispatch, "book_location.error.fetch_apartments");
        dispatch(setLoader(false));
        return undefined;
      }

      // Tenant-scoped exclusion (Neuland customer request).
      // TODO: move to backend filtering — tracked in PR #805 review §13.
      if (getTenantName()?.toLowerCase() === "neuland") {
        apartments = apartments.filter(
          (apt: { id: string }) =>
            apt.id !== "612b9a7e-40ca-4686-a9ed-7161781419cf"
        );
      }
      // Read from the store at call time, not from this hook's render: the
      // page records the flag in a mount effect, which the first search can
      // otherwise overtake.
      dispatch(
        categoryNoticeSet(
          categoryNoticeFor(
            data?.data?.categoryNotice,
            baseStore.getState().apartment.categoryRejectedOnEntry
          )
        )
      );
      dispatch(setAllApartment(apartments));
      dispatch(setLoader(false));
      return apartments;
    } catch (error) {
      Logger.for("fetchApartments").error(error);
      dispatch(setLoader(false));
      ErrorTracker.trackException(error as Error);
      dispatch(apartmentsLoadFailed());
      notifyLoadFailure(dispatch, "book_location.error.fetch_apartments");
    }
  };

  return fetchApartments;
};
